// holdingGeometry — render a CIFP holding pattern as a GeoJSON LineString.
//
// Standard FAA / ICAO holding pattern:
//   - Fly inbound on the inbound course to the holding fix
//   - At the fix, turn (right by default, left if turn=L) 180°
//   - Fly outbound for the leg duration (1 min below 14k ft, 1.5 min above)
//     or fixed distance for DME holds
//   - Turn 180° to re-intercept the inbound course
//
// We model the racetrack as a 4-corner rectangle in lat/lon space using
// destinationPoint() great-circle projection at a representative ground
// speed. The two end semicircles are sampled into ~12 points each so the
// shape reads as smooth at typical zoom levels.
//
// At cruise altitudes a standard turn is rate-1 (3°/sec → 2 minutes for
// 360° → 1 nm radius at 110 kt). We assume a HOLD_SPEED_KT for time-based
// holds to convert minutes → nm.

import { destinationPoint } from './geo';

const HOLD_SPEED_KT = 110;
const HOLD_TURN_RADIUS_NM = 1.0;

export interface HoldParams {
  /** Fix lat/lon (the holding fix). */
  fixLat: number;
  fixLon: number;
  /** Inbound course (true or magnetic — both work for short distances). */
  inboundCourseDeg: number;
  /** 'L' or 'R'. Default 'R' if unknown. */
  turnDirection: 'L' | 'R' | null;
  /** Leg length: time-based (minutes) or distance-based (nm). */
  leg: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null;
}

export interface LngLat {
  lon: number;
  lat: number;
}

/** Compute the racetrack outline as an ordered list of [lon, lat] coords.
 *  Starts and ends at the holding fix so the LineString closes visually
 *  without needing to be a polygon. */
export function holdingRacetrackCoords(p: HoldParams): Array<[number, number]> {
  const turn: 'L' | 'R' = p.turnDirection === 'L' ? 'L' : 'R';
  const legNm = legLengthNm(p.leg);

  // The inbound course is the direction the aircraft is FLYING INTO the
  // fix. The outbound course is the reciprocal.
  const inbound = p.inboundCourseDeg;
  const outbound = (inbound + 180) % 360;
  // Perpendicular to the inbound (away from the protected side). For a
  // standard right-hand hold, the racetrack lies to the RIGHT of the
  // inbound course, i.e. inbound + 90°. For a left-hand hold, inbound - 90°.
  const perpOut = turn === 'R' ? (inbound + 90) % 360 : (inbound + 270) % 360;

  // Step 1: from fix, fly perpendicular by 2 × turn radius to find the
  // OTHER inbound endpoint (start of outbound leg / end of outbound turn).
  const turnCenterAtFix = destinationPoint(p.fixLat, p.fixLon, perpOut, HOLD_TURN_RADIUS_NM);
  const fixSideEnd      = destinationPoint(turnCenterAtFix.lat, turnCenterAtFix.lon, perpOut, HOLD_TURN_RADIUS_NM);
  // Step 2: from the fix-side end, project BACKWARD along outbound by legNm
  // to find the far corner of the rectangle on the same side as fixSideEnd.
  const farSideEnd = destinationPoint(fixSideEnd.lat, fixSideEnd.lon, outbound, legNm);
  // Step 3: also need the far corner on the FIX side: from the fix,
  // project along outbound by legNm.
  const farSideFix = destinationPoint(p.fixLat, p.fixLon, outbound, legNm);

  // Now we have four corners of the racetrack:
  //   fix       → near corner on the inbound side
  //   fixSideEnd→ near corner on the outbound side (opposite the fix)
  //   farSideEnd→ far  corner on the outbound side
  //   farSideFix→ far  corner on the inbound side (same line as fix)
  //
  // Path: fix → farSideFix → semicircle to farSideEnd → fixSideEnd → semicircle back to fix.
  const out: Array<[number, number]> = [];
  out.push([p.fixLon, p.fixLat]);
  out.push([farSideFix.lon, farSideFix.lat]);
  // Far-side semicircle: 180° turn from outbound to inbound, centered at
  // the midpoint between farSideFix and farSideEnd.
  const farCenter = midpoint(farSideFix, farSideEnd);
  const farRadius = HOLD_TURN_RADIUS_NM;
  // Start bearing is outbound (we're traveling outbound at this point);
  // turn direction is the hold's primary turn direction. After 180° we're
  // heading inbound. We sample 12 intermediate points.
  for (const c of semicircle(farCenter, farRadius, outbound, turn, 12)) {
    out.push([c.lon, c.lat]);
  }
  out.push([fixSideEnd.lon, fixSideEnd.lat]);
  // Fix-side semicircle: turn back to outbound direction, then we're flying
  // outbound again from the fix... wait, on the FIX side we're transitioning
  // from inbound (just arrived at fix) to outbound. Center is between fix
  // and fixSideEnd.
  const fixCenter = midpoint({ lat: p.fixLat, lon: p.fixLon }, fixSideEnd);
  for (const c of semicircle(fixCenter, farRadius, inbound, turn, 12)) {
    out.push([c.lon, c.lat]);
  }
  out.push([p.fixLon, p.fixLat]);
  return out;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function legLengthNm(leg: HoldParams['leg']): number {
  if (!leg) return (1 / 60) * HOLD_SPEED_KT; // 1 minute default
  if (leg.kind === 'time') return (leg.minutes / 60) * HOLD_SPEED_KT;
  return leg.nm;
}

function midpoint(a: LngLat, b: LngLat): LngLat {
  // Acceptable straight-arithmetic midpoint at the small scales involved
  // (≤ 5 nm). For high-latitude accuracy we'd use a great-circle mid.
  return { lat: (a.lat + b.lat) / 2, lon: (a.lon + b.lon) / 2 };
}

/** Sample N points along a semicircle of radius `radiusNm` around `center`,
 *  starting at compass bearing `startBearing` and turning 180° in `dir`.
 *  Returns the intermediate points (does NOT include the endpoints).
 */
function semicircle(
  center: LngLat,
  radiusNm: number,
  startBearing: number,
  dir: 'L' | 'R',
  samples: number,
): LngLat[] {
  // Sample N intermediate points on a 180° arc starting at the point
  // located perpendicular to `startBearing` (turn-center side) at the
  // entry of the arc, sweeping 180° to the diametrically opposite
  // point at the exit of the arc.
  //
  // `dir` is the hold's airplane-turn direction (R = right turns,
  // L = left turns). We trace the racetrack OUTLINE in the direction
  // opposite the airplane flight — i.e., the outline traversal at this
  // arc is the geometric reverse of the airplane's rotation. The arc
  // must therefore BULGE on the side OPPOSITE the racetrack body. For
  // an R-turn hold the body is on the right of inbound; the far/near
  // turn arcs must bulge away from the body — i.e., sweep CCW in
  // bearing terms when drawing outline.
  //
  // outwardFromStart = bearing from center to the entry endpoint of
  // the arc. For R, that's `startBearing + 90` (right of travel
  // direction was the racetrack body — but the outline ENTERS the
  // arc from the body side). For L, mirror.
  //
  // Sweep sign: R hold -> CCW outline (decreasing bearing).
  //             L hold -> CW outline (increasing bearing).
  //
  // Before Bug #7 (2026-05-16) the sweep sign was flipped, producing
  // racetrack ends that bulged INWARD (concave "pinched pill") instead
  // of outward (convex stadium).
  const outwardFromStart = dir === 'R'
    ? (startBearing + 90) % 360
    : (startBearing + 270) % 360;
  const sweepSign = dir === 'R' ? -1 : +1;
  const out: LngLat[] = [];
  for (let i = 1; i < samples; i++) {
    const frac = i / samples;
    const sweep = 180 * frac;
    const angle = (outwardFromStart + sweepSign * sweep + 360) % 360;
    out.push(destinationPoint(center.lat, center.lon, angle, radiusNm));
  }
  return out;
}
